import { Platform } from 'react-native'

import Alert from 'app/services/Alert'
import { isOfType } from 'app/utils/helpers/type.helpers'

import {
  FileModel,
  FileValue,
  UploadValue,
  UploadValueModel
} from './UploadField.types'

export const transformValueToFiles = (
  value: UploadValue,
  multiple: boolean
) => {
  const convertValueToFile = (value: UploadValueModel) => {
    return {
      id: value.id || generateId(),
      name: value.name || '',
      size: value.size || 0,
      url: value.url,
      loading: false,
      loaded: false
    }
  }

  if (isOfType.array(value)) {
    const activeValues = value
      .filter(({ _destroy }) => !_destroy)
      .sort((a, b) => (a.position || 1) - (b.position || 1))

    return activeValues.map((v) => convertValueToFile(v))
  }

  if (multiple) return []

  if (value) {
    return convertValueToFile(value)
  }

  return value || null
}

export const transformFilesToValue = (
  files: FileValue,
  fromValues: UploadValue
) => {
  const convertFileToValue = (file: FileModel) => {
    const isNewFile = !!file.loaded

    return {
      name: file.name,
      size: file.size,
      url: file.url,
      ...(!isNewFile && { id: file.id }),
      ...(isNewFile && { image: file.payload.signed_id }),
      ...(isNewFile && { awsUrl: file.payload.awsUrl })
    }
  }

  if (isOfType.array(files)) {
    const currentFiles = (fromValues || [])?.reduce(
      (acc, value) => {
        if (value._destroy) {
          acc.destroyedFiles.push({
            ...value,
            _destroy: true
          })
        } else {
          acc.activeFiles.push(value)
        }

        return acc
      },
      {
        activeFiles: [],
        destroyedFiles: []
      }
    )

    const newValuesFromFile = files.map((v) => convertFileToValue(v))

    const newValues = newValuesFromFile.reduce<any>(
      (acc, value, index) => {
        const indexFile = acc.destroyedFiles.findIndex(
          ({ id, image }) => (id || image) === (value.id || value.image)
        )

        if (indexFile !== -1) {
          const file = acc.destroyedFiles[indexFile]
          acc.activeFiles.push({
            ...file,
            position: index + 1,
            _destroy: false
          })
          acc.destroyedFiles.splice(indexFile, 1)
        } else {
          acc.activeFiles.push({
            ...value,
            position: index + 1,
            _destroy: false
          })
        }

        return acc
      },
      {
        activeFiles: [],
        destroyedFiles: currentFiles.activeFiles
      }
    )

    const destroyedFiles = [
      ...currentFiles.destroyedFiles,
      ...newValues.destroyedFiles
        .filter(({ image }) => !image)
        .map((detroyedFile) => ({
          id: detroyedFile?.id,
          _destroy: true
        }))
    ]

    return [...newValues.activeFiles, ...destroyedFiles]
  }

  const fromValue = fromValues as UploadValueModel | null
  const canDestroy = fromValue?._destroy || fromValue?.id
  const isEmpty = !files

  const newFile = convertFileToValue(
    isEmpty ? { id: '', url: '', size: 0 } : files
  )

  if (canDestroy) {
    return {
      ...newFile,
      position: 0,
      id: String(fromValue?.id),
      ...(!newFile.image &&
        !newFile.url && {
          _destroy: true
        })
    }
  }

  return isEmpty ? null : newFile
}

export const generateId = () => {
  return Math.random().toString(36).substr(2, 9)
}

export const getUrlFromFile = (file: File) => {
  if (Platform.OS === 'web') {
    return (URL as any).createObjectURL(file)
  }

  return null
}

export const allSettled = (promises) =>
  Promise.all(
    promises.map((promise) =>
      promise
        .then((value) => ({ state: 'fulfilled', value }))
        .catch((reason) => ({ state: 'rejected', reason }))
    )
  )

export const uploadFieldValidator = ({ size, maxSize, formatMessage }) => {
  if (size > maxSize) {
    const message = formatMessage(
      { defaultMessage: 'Size is larger than {value}' },
      { value: humanFileSizeFormat(maxSize) }
    )

    Alert.error(message)

    return {
      code: 'size-too-large',
      message: message
    }
  }

  return null
}

export const humanFileSizeFormat = (size: number) => {
  const i = Math.floor(Math.log(size) / Math.log(1024))

  return (
    (size / Math.pow(1024, i)).toFixed(0) +
    ' ' +
    ['B', 'kB', 'Mb', 'Gb', 'Tb'][i]
  )
}

export const isEmptyInitialFiles = (initialFiles: FileValue): boolean => {
  return Boolean(
    isOfType.array(initialFiles) ? initialFiles.length : initialFiles
  )
}
